import { TableDataHeaders } from 'types/data-tables/column-by-key.js';
import { CellInfo } from 'utilities/buildTables/useTable.js';
import { IDataCell_Default_Primitive } from './cell-build-data/buildDataPrimitive.js';
// import { IDataCell_Primitive_Formatted } from './cell-build-data/buildDataPrimitiveFormatted';
import { IDataCell_Rainfall } from './cell-build-data/buildDataRainfall.js';
import { IDataCell_StatusRating } from './cell-build-data/buildDataStatus.js';
import { IDataCell_Link } from './cell-build-data/buildLink.js';
import { IDataCell_Date, RenderDate } from './cell-build-ui/date.js';
import { RenderDateString } from './cell-build-ui/dateString.js';
import { RenderRainfall } from './cell-build-ui/rainfall.js';
import { CellRenderType, ComponentTableTypes } from './cell-build-ui/renderCell.js';
import { RenderCellLink } from './cell-build-ui/renderLink.js';
import { RenderPrimitive, RenderPrimitiveBold } from './cell-build-ui/renderPrimitive.js';
import { RenderStatusRating } from './cell-build-ui/renderStatusRating.js';
import { RenderWindChill } from './cell-build-ui/windChill.js';

export type TupleToObject<T extends [string, unknown]> = { [key in T[0]]: Extract<T, [key, unknown]>[1] };

/** Used for building data objects / stories */
export type GenericForecastTableRender<T extends Record<string, [string, CellRenderType]>> = GenericTableTypeRender<
  T[keyof T]
>;
type GenericTableTypeRender<T extends [string, CellRenderType]> = {
  tableHeaders: TableDataHeaders<'Raw'> & {
    id: T[0];
    render: T[1];
  };
  tableData: Partial<{
    [P in keyof TupleToObject<T>]: ICellLookup<'Render'>[TupleToObject<T>[P]];
  }>;
};

export type GenericForecastTableRaw<T extends Record<string, [string, CellRenderType]>> = GenericTableTypeRaw<
  T[keyof T]
>;
type GenericTableTypeRaw<T extends [string, CellRenderType]> = {
  tableHeaders: TableDataHeaders<'Raw'> & {
    id: T[0];
    render: T[1];
  };
  tableData: Partial<{
    [P in keyof TupleToObject<T>]: ICellLookup<'Raw'>[TupleToObject<T>[P]];
  }>;
};

export type CoreCellProps = { cellInfo: CellInfo; getCellClassNames: (cellInfo: CellInfo) => string };

/** Ensures that no unhandled cases reach here */
export function assertUnreachable(x: never): never {
  throw new Error(`Didn't expect ${x} to get here`);
}

export type ICellTypes<T extends 'Raw' | 'Render'> = {
  1: [CellRenderType.date, IDataCell_Date<T>];
  2: [CellRenderType.dateString, IDataCell_Date<T>];
  3: [CellRenderType.rainfall, IDataCell_Rainfall<T>];
  4: [CellRenderType.windchill, IDataCell_Default_Primitive<T>];
  5: [CellRenderType.renderString, IDataCell_Default_Primitive<T>];
  6: [CellRenderType.stringBold, IDataCell_Default_Primitive<T>];
  8: [CellRenderType.statusRating, IDataCell_StatusRating<T>];
  10: [CellRenderType.link, IDataCell_Link<T>];
};

export const getDefaultComponents = (): ComponentTableTypes => ({
  [CellRenderType.date]: RenderDate,
  [CellRenderType.dateString]: RenderDateString,
  [CellRenderType.rainfall]: RenderRainfall,
  [CellRenderType.windchill]: RenderWindChill,
  [CellRenderType.renderString]: RenderPrimitive,
  [CellRenderType.stringBold]: RenderPrimitiveBold,
  [CellRenderType.statusRating]: RenderStatusRating,
  [CellRenderType.link]: RenderCellLink,
});

export type ICellTuples<T extends 'Raw' | 'Render'> = ICellTypes<T>[keyof ICellTypes<T>];

export type ICellLookup<T extends 'Raw' | 'Render'> = {
  [P in CellRenderType]: TupleToObject<ICellTuples<T>>[P];
};

export type IdCellTypeRawValMap = Record<string, ICellTuples<'Raw'>[1] | undefined>;

export type ICellTypeValues = ICellLookup<'Render'>[keyof ICellLookup<'Render'>];
